import React, { Suspense, lazy, useEffect, createContext, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { Analytics } from '@vercel/analytics/react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import './App.css';
import UserProfilePage from './components/UserProfilePage';
import ProductPage from './components/ProductPage';
import CategoryPage from './components/CategoryPage';
import CheckoutPage from './components/CheckoutPage';
import OrderFinalization from './components/OrderFinalization';
import { API_BASE_URL } from './config';
import PageBackground from './components/PageBackground';
import CookieConsent from './components/CookieConsent';

// Create API context for global request management
export const ApiContext = createContext();

// API request manager to handle rate limiting and caching
const ApiRequestManager = ({ children }) => {
  const [requestLog, setRequestLog] = useState({});
  const [backoffTime, setBackoffTime] = useState({});
  
  // Global cache for API responses
  const [responseCache, setResponseCache] = useState({});
  
  // Cache duration in milliseconds (1 minute)
  const CACHE_DURATION = 60000;
  
  // Minimum time between requests to the same endpoint
  const REQUEST_INTERVAL = 1000;
  
  // Handle API requests with rate limiting protection
  const fetchWithProtection = async (url, options = {}) => {
    try {
      const now = Date.now();
      const endpoint = url.split('?')[0]; // Remove query params for endpoint tracking
      
      // Check if we're in backoff period for this endpoint
      if (backoffTime[endpoint] && now < backoffTime[endpoint]) {
        console.log(`Endpoint ${endpoint} in backoff until ${new Date(backoffTime[endpoint])}`);
        throw new Error('Rate limited. Please try again later.');
      }
      
      // Check if we should use cached response
      const cacheKey = `${url}:${JSON.stringify(options.body || {})}`;
      const cachedResponse = responseCache[cacheKey];
      
      if (cachedResponse && now - cachedResponse.timestamp < CACHE_DURATION) {
        console.log(`Using cached response for ${url}`);
        return cachedResponse.data;
      }
      
      // Check if we need to wait due to recent request
      const lastRequest = requestLog[endpoint] || 0;
      if (now - lastRequest < REQUEST_INTERVAL) {
        const waitTime = REQUEST_INTERVAL - (now - lastRequest);
        console.log(`Waiting ${waitTime}ms before requesting ${endpoint}`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      // Update request log
      setRequestLog(prev => ({
        ...prev,
        [endpoint]: Date.now()
      }));
      
      // Make the request
      const response = await fetch(url, options);
      
      // Handle rate limiting
      if (response.status === 429) {
        // Set exponential backoff for this endpoint
        const newBackoff = now + 60000; // 1 minute minimum
        setBackoffTime(prev => ({
          ...prev,
          [endpoint]: newBackoff
        }));
        console.log(`Rate limited on ${endpoint}, backing off until ${new Date(newBackoff)}`);
        throw new Error('Rate limited. Please try again later.');
      }
      
      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }
      
      // Cache successful responses
      if (response.ok && options.method !== 'POST' && options.method !== 'PUT' && options.method !== 'DELETE') {
        setResponseCache(prev => ({
          ...prev,
          [cacheKey]: {
            data,
            timestamp: now
          }
        }));
      }
      
      // Reset backoff on successful request
      if (backoffTime[endpoint]) {
        setBackoffTime(prev => {
          const newBackoff = { ...prev };
          delete newBackoff[endpoint];
          return newBackoff;
        });
      }
      
      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  };
  
  // Clear cache for specific endpoints or all
  const clearCache = (endpoint = null) => {
    if (endpoint) {
      setResponseCache(prev => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach(key => {
          if (key.startsWith(endpoint)) {
            delete newCache[key];
          }
        });
        return newCache;
      });
    } else {
      setResponseCache({});
    }
  };
  
  return (
    <ApiContext.Provider value={{ fetchWithProtection, clearCache }}>
      {children}
    </ApiContext.Provider>
  );
};

// Lazy-loaded components
const HomePage = lazy(() => import('./components/HomePage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const RegisterPage = lazy(() => import('./components/RegisterPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const VerifyEmailPage = lazy(() => import('./components/VerifyEmailPage'));
const Paczki = lazy(() => import('./components/Paczki'));
const OrderConfirmation = lazy(() => import('./components/OrderConfirmation'));

// Layout component to handle Footer visibility
const Layout = ({ showFooter = true, children }) => (
  <>
    {children}
    {showFooter && <Footer />}
  </>
);

// Create a new component to handle page transitions
const PageTransition = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Reset scroll position
    window.scrollTo(0, 0);
    
    // Fade in animation for new content with no scroll manipulation
    gsap.fromTo('.ContentWrapper > *',
      { opacity: 0 },
      { 
        opacity: 1, 
        duration: 0.3,
        ease: 'power2.out'
      }
    );
  }, [location]);

  return (
    <div className="page-transition-wrapper">
      {children}
    </div>
  );
};

// Komponent do śledzenia zachowań użytkownika
function UsabilityTracker() {
  const location = useLocation();

  useEffect(() => {
    const trackPageView = async () => {
      try {
        // Check analytics consent before tracking
        const consent = localStorage.getItem('cookie_consent');
        if (!consent) return;

        const preferences = JSON.parse(consent);
        if (!preferences.analytics) return; // Respect user's choice

        const path = location.pathname;
        
        // Zbieraj anonimowe dane o zachowaniach użytkowników
        const data = {
          path,
          timestamp: new Date().toISOString(),
          referrer: document.referrer,
          screenWidth: window.innerWidth,
          screenHeight: window.innerHeight,
          userAgent: navigator.userAgent
        };
        
        // Dodaj userId tylko jeśli użytkownik zgodził się na marketing
        const userId = localStorage.getItem('userId');
        if (userId && preferences.marketing) {
          data.userId = userId;
        }
        
        // Zapisz dane analityczne tylko jeśli użytkownik wyraził zgodę
        const analytics = JSON.parse(localStorage.getItem('_usability_analytics') || '[]');
        analytics.push(data);
        if (analytics.length > 50) analytics.shift();
        localStorage.setItem('_usability_analytics', JSON.stringify(analytics));
        
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    };

    trackPageView();
  }, [location]);

  return null;
}

// Authentication context
export const AuthContext = createContext();

// AuthProvider component
const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  
  // Function to check if a token is expired
  const isTokenExpired = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Add 30 second buffer to prevent edge cases
      return (payload.exp * 1000) < (Date.now() - 30000);
    } catch (e) {
      console.error('Error parsing token:', e);
      return true; // If we can't parse it, assume it's expired
    }
  };
  
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsAuthenticated(false);
        setIsAuthChecking(false);
        return;
      }
      
      try {
        // Try to parse token for a quick check
        const payload = JSON.parse(atob(token.split('.')[1]));
        
        // Check if token is expired
        if (isTokenExpired(token)) {
          console.log('Token expired, logging out');
          // Token expired, remove from storage
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('userRole');
          setIsAuthenticated(false);
          setUserRole(null);
          setUserId(null);
          setIsAuthChecking(false);
          return;
        }
        
        // Set auth state from token without waiting for server validation
        // This prevents the flash of unauthenticated state on page refresh
        setIsAuthenticated(true);
        setUserRole(payload.role || 'user');
        setUserId(payload.id || payload.userId || payload.sub);
        setIsAuthChecking(false);
        
        // Then validate on server in the background, but don't wait for it
        // and don't log out if it fails due to network issues
        fetch(`${API_BASE_URL}/api/users/validate-token`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          // Add a cache buster to prevent caching
          cache: 'no-store'
        })
        .then(response => {
          // Only log out if server explicitly says token is invalid with 401
          if (response.status === 401) {
            console.log('Server rejected token, logging out');
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('userRole');
            setIsAuthenticated(false);
            setUserRole(null);
            setUserId(null);
          }
        })
        .catch(error => {
          // If server validation fails due to network issues, keep local validation
          console.warn('Server validation failed, keeping local token validation', error);
        });
      } catch (error) {
        console.error('Auth check error:', error);
        // Don't log out on token parse errors, only set authenticated to false
        // This allows the app to retry on next refresh
        setIsAuthenticated(false);
        setUserRole(null);
        setUserId(null);
        setIsAuthChecking(false);
      }
    };
    
    checkAuthStatus();
    
    // Set up interval to periodically check token expiration
    const tokenCheckInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && isTokenExpired(token)) {
        console.log('Token expired during session, logging out');
        logout();
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(tokenCheckInterval);
  }, []);
  
  const login = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userId', user.id);
    localStorage.setItem('userRole', user.role || 'user');
    setIsAuthenticated(true);
    setUserRole(user.role || 'user');
    setUserId(user.id);
    
    // Clear guest cart if any exists
    localStorage.removeItem('guestCart');
  };
  
  const logout = () => {
    // Save cart data before logout if possible
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      
      if (userId && token) {
        // Fetch current cart to save as guest cart
        fetch(`${API_BASE_URL}/api/cart/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        .then(res => {
          if (res.ok) return res.json();
          throw new Error('Failed to fetch cart for logout');
        })
        .then(cartData => {
          if (cartData && cartData.items && cartData.items.length > 0) {
            localStorage.setItem('guestCart', JSON.stringify(cartData));
          }
        })
        .catch(err => console.warn('Could not save cart before logout:', err));
      }
    } catch (e) {
      console.warn('Error saving cart on logout:', e);
    }
    
    // Clear auth data
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    setIsAuthenticated(false);
    setUserRole(null);
    setUserId(null);
  };
  
  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isAuthChecking, 
      userRole,
      userId,
      login, 
      logout,
      isTokenExpired
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected route component
const ProtectedRoute = ({ element, requiredRole }) => {
  const { isAuthenticated, isAuthChecking, userRole } = React.useContext(AuthContext);
  const location = useLocation();
  
  if (isAuthChecking) {
    return <div className="loading-auth">Sprawdzanie autoryzacji...</div>;
  }
  
  if (!isAuthenticated) {
    // We no longer save location for redirect, always go to home page after login
    localStorage.removeItem('returnUrl');
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to="/" replace />;
  }
  
  return element;
};

function App() {
  useEffect(() => {
    // Check cookie consent for recently viewed products
    const checkCookieConsent = () => {
      const consent = localStorage.getItem('cookie_consent');
      if (!consent) return;

      const preferences = JSON.parse(consent);
      if (!preferences.functional) {
        // If user didn't consent to functional cookies, remove related data
        localStorage.removeItem('recentlyAddedProducts');
      }
    };

    checkCookieConsent();
  }, []);

  return (
    <Router>
      <ApiRequestManager>
        <AuthProvider>
          <div className="AppContainer">
            <PageBackground />
            <Navbar />
            <div className="ContentWrapper">
              <PageTransition>
                <Suspense fallback={<div>Ładowanie...</div>}>
                  <Routes>
                    <Route path="/" element={<Layout><HomePage /></Layout>} />
                    <Route path="/admin/*" element={
                      <ProtectedRoute 
                        element={<Layout showFooter={false}><AdminPage /></Layout>} 
                        requiredRole="admin" 
                      />
                    } />
                    <Route path="/login" element={<Layout><LoginPage /></Layout>} />
                    <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
                    <Route path="/profile" element={
                      <ProtectedRoute 
                        element={<Layout><UserProfilePage /></Layout>} 
                      />
                    } />
                    <Route path="/product/:slug" element={<Layout><ProductPage /></Layout>} />
                    <Route path="/category/:categoryId" element={<Layout><CategoryPage /></Layout>} />
                    <Route path="/checkout" element={
                      <ProtectedRoute 
                        element={<Layout><CheckoutPage /></Layout>} 
                      />
                    } />
                    <Route path="/finalize-order" element={
                      <ProtectedRoute 
                        element={<Layout><OrderFinalization /></Layout>} 
                      />
                    } />
                    <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
                    <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
                    <Route path="/verify-email" element={<Layout><VerifyEmailPage /></Layout>} />
                    <Route path="/paczki" element={<Layout><Paczki /></Layout>} />
                    <Route path="/order-confirmation" element={
                      <ProtectedRoute 
                        element={<Layout><OrderConfirmation /></Layout>} 
                      />
                    } />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </Suspense>
              </PageTransition>
            </div>
            <UsabilityTracker />
            {/* Only load Vercel Analytics if user consented */}
            {JSON.parse(localStorage.getItem('cookie_consent') || '{}').analytics && <Analytics />}
            <CookieConsent />
          </div>
        </AuthProvider>
      </ApiRequestManager>
    </Router>
  );
}

export default App;
